import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import addAtomsToInventory from '@/shared/api/inventory/addAtomsToInventory';
import createInventory from '@/shared/api/inventory/createInventory';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useProgressDialog from '@/shared/hooks/useProgressDialog';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import DialogName from '@/shared/types/dialogName.enum';
import { InventoryGetModel } from '@/shared/types/inventory.type';

type OnUpdateNotificationBar = ReturnType<
  typeof useNotificationBar
>['onUpdateNotificationBar'];

type OnUpdateProgressDialog = ReturnType<
  typeof useProgressDialog
>['onUpdateProgressDialog'];

type OnUpdateDialogState = ReturnType<
  typeof useModalDialog<unknown, DialogName>
>['onUpdateDialogState'];

type Args = {
  onUpdateNotificationBar: OnUpdateNotificationBar;
  onUpdateProgressDialog: OnUpdateProgressDialog;
  onUpdateDialogState: OnUpdateDialogState;
};

const useCreateInventory = ({
  onUpdateNotificationBar,
  onUpdateProgressDialog,
  onUpdateDialogState,
}: Args) => {
  const { t } = useTranslation(['inventory', 'common']);
  const redirectLogin = useRedirectLogin();

  const [shouldPersistForm, setShouldPersistForm] = useState(false);
  const [tryResetForm, setTryResetForm] = useState(false);
  const [keyToResetForm, setKeyToResetForm] = useState('');

  const [createdInventoryItem, setCreatedInventoryItem] =
    useState<InventoryGetModel | null>(null);

  const onCreateInventory = async (details: Record<string, unknown>) => {
    onUpdateProgressDialog({
      title: t('inventory:creatingInventory'),
      open: true,
      progress: 0,
    });

    const INVENTORY_ITEMS_LIMIT = 1000;

    const isQuantityInRange = (details.qty as number) <= INVENTORY_ITEMS_LIMIT;

    if (isQuantityInRange) {
      const response = await createInventory({
        body: details,
        onUnauthorized: redirectLogin,
      });

      if (response.success) {
        setTryResetForm(true);

        onUpdateProgressDialog({ open: false });
        onUpdateNotificationBar({
          open: true,
          message: t('inventory:createdSuccessfully'),
        });

        setCreatedInventoryItem(response.data as InventoryGetModel);
      } else {
        onUpdateProgressDialog({ open: false });
        onUpdateDialogState(DialogName.Error, {
          open: true,
          errors: [response.msg],
        });
      }
    } else {
      const response = await createInventory({
        body: details,
        onUnauthorized: redirectLogin,
      });

      if (response.success) {
        setTryResetForm(true);

        const addInventoryItemsResponse = await addAtomsToInventory({
          inventoryId: (response.data as InventoryGetModel).inventory_id,
          body: details.atomic_metadata,
          qty: (details.qty as number) - INVENTORY_ITEMS_LIMIT,
          onUnauthorized: redirectLogin,
          onProgress: (progress) => {
            onUpdateProgressDialog({ progress: progress });
          },
        });

        if (addInventoryItemsResponse.every((response) => response.success)) {
          setTryResetForm(true);
          setCreatedInventoryItem(response.data as InventoryGetModel);
          onUpdateProgressDialog({ open: false });
          onUpdateNotificationBar({
            open: true,
            message: t('inventory:createdSuccessfully'),
          });
        }
      } else {
        onUpdateDialogState(DialogName.Error, {
          open: true,
          errors: [response.msg],
        });
      }
    }
  };

  useEffect(() => {
    if (tryResetForm && !shouldPersistForm) {
      setKeyToResetForm(Date.now().toString());
      setTryResetForm(false);
    }
  }, [shouldPersistForm, tryResetForm]);

  return {
    shouldPersistForm,
    keyToResetForm,
    createdInventoryItem,
    setShouldPersistForm,
    onCreateInventory,
  };
};

export default useCreateInventory;
